import { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Container, 
  Grid2, 
  Card, 
  CardContent, 
  CardHeader, 
  Divider, 
  Button, 
  Tabs, 
  Tab, 
  Chip,
  Avatar,
  LinearProgress,
  alpha,
  useTheme
} from '@mui/material';
import BasePage from '../../pages/BasePage';
import { useBusiness } from '../../contexts/BusinessContext';
import { useBusinessAdmin } from '../../contexts/BusinessAdminContext';
import AddAgentForm from './AddAgentForm';
import AgentsTable from './AgentsTable';
import RecentActivityTable from './RecentActivityTable';
import { Agent, AdminStatsCard } from '../../types/business';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import BusinessIcon from '@mui/icons-material/Business';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import HomeIcon from '@mui/icons-material/Home';
import GroupIcon from '@mui/icons-material/Group';

// Mock data for demo purposes (keep existing mock data)
const MOCK_AGENTS = [
  { id: 1, name: 'Jennifer Smith', email: 'jennifer@example.com', phone: '(555) 123-4567', clients: 12, activeClients30d: 8, status: 'active', walkthroughs30d: 18 },
  { id: 2, name: 'Michael Johnson', email: 'michael@example.com', phone: '(555) 234-5678', clients: 8, activeClients30d: 6, status: 'active', walkthroughs30d: 14 },
  { id: 3, name: 'Sarah Williams', email: 'sarah@example.com', phone: '(555) 345-6789', clients: 5, activeClients30d: 2, status: 'inactive', walkthroughs30d: 3 },
  { id: 4, name: 'David Brown', email: 'david@example.com', phone: '(555) 456-7890', clients: 10, activeClients30d: 9, status: 'active', walkthroughs30d: 22 },
];

const MOCK_PERFORMANCE = {
  totalListings: 42,
  activeListings: 28,
  soldLastMonth: 8,
  revenue: 124500,
  growth: 18.5,
  clientSatisfaction: 94,
  propertiesAnalyzedThisMonth: 57,
  avgPropertiesPerAgent: 14.3,
  topAnalyzer: 'David Brown'
};

const BusinessAdminPage = () => {
  const theme = useTheme();
  const { business } = useBusiness();
  const { 
    agents, 
    isLoadingAgents, 
    agentsError, 
    totalAgents, 
    addAgent, 
    isAddingAgent,
    updateAgent,
    isUpdatingAgent,
    // Add business stats
    stats,
    isLoadingStats
  } = useBusinessAdmin();
  
  const [activeTab, setActiveTab] = useState(0);
  const [addAgentModalOpen, setAddAgentModalOpen] = useState(false);
  const [editAgentModalOpen, setEditAgentModalOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleAddAgentOpen = () => {
    setAddAgentModalOpen(true);
  };

  const handleAddAgentClose = () => {
    setAddAgentModalOpen(false);
  };

  const handleEditAgentOpen = (agent: Agent) => {
    setEditingAgent(agent);
    setEditAgentModalOpen(true);
  };

  const handleEditAgentClose = () => {
    setEditAgentModalOpen(false);
    setEditingAgent(null);
  };

  const handleAddAgentSubmit = async (agentData: {
    userEmail: string;
    agentName: string;
    agentEmail?: string;
    agentPhone?: string;
    isActive: boolean;
  }) => {
    return await addAgent(agentData);
  };

  const handleEditAgentSubmit = async (
    userEmail: string,
    updates: {
      agentName?: string;
      agentEmail?: string;
      agentPhone?: string;
      agentProfile?: string;
      agentProfileUrl?: string;
      agentProfileImage?: string;
      isActive?: boolean;
    }
  ) => {
    return await updateAgent(userEmail, updates);
  };

  // Helper function to get display value with loading state
  const getDisplayValue = (realValue: number | undefined, mockValue: number, isLoading: boolean) => {
    if (isLoading) return '...';
    return realValue !== undefined ? realValue : mockValue;
  };

  const statsData: AdminStatsCard[] = [
    {
      title: "Active Agents",
      value: getDisplayValue(
        stats?.agents?.active_agents, 
        MOCK_AGENTS.filter(a => a.status === 'active').length,
        isLoadingStats
      ),
      change: stats?.agents?.total_agents ? `${stats.agents.total_agents} total` : null,
      icon: <PeopleAltIcon />,
      color: theme.palette.success.main
    },
    {
      title: "Total Clients",
      value: getDisplayValue(
        stats?.clients?.total_clients,
        MOCK_AGENTS.reduce((sum, agent) => sum + agent.clients, 0),
        isLoadingStats
      ),
      change: stats?.clients?.active_clients ? `${stats.clients.active_clients} active` : null,
      newClientsChip: stats?.clients?.clients_last_30_days ? `+${stats.clients.clients_last_30_days} this month` : null,
      icon: <PeopleAltIcon />,
      color: theme.palette.primary.main
    },
    {
      title: "Homes Added (30d)",
      value: getDisplayValue(
        stats?.homes?.homes_last_30_days,
        MOCK_PERFORMANCE.activeListings,
        isLoadingStats
      ),
      change: stats?.homes?.active_homes_last_30_days ? `${stats.homes.active_homes_last_30_days} active homes` : null,
      icon: <HomeIcon />,
      color: theme.palette.warning.main
    }
  ];

  // Use real agents data only - no mock fallback
  const displayAgents = agents;

  return (
    <BasePage
      title="Business Admin | CribCritic"
      description="Manage your business settings and agents"
      keywords="business admin, property management, agents"
    >
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Business Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your real estate business, agents, and client relationships
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<GroupIcon />}
              href="/business/admin/referrals"
              sx={{ borderRadius: 2 }}
            >
              Referrals
            </Button>
            {/* <Button
              variant="contained"
              color="primary"
              startIcon={<SettingsIcon />}
              sx={{ borderRadius: 2 }}
            >
              Business Settings
            </Button> */}
          </Box>
        </Box>

        {/* Business Info Card */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            mb: 4, 
            borderRadius: 2,
            background: theme => `linear-gradient(120deg, ${alpha(theme.palette.primary.light, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
            border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', mr: 2 }}>
                <BusinessIcon fontSize="large" />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold">{business?.name || 'CribCritic Test Business'}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <Chip 
                    label={business?.status || 'Active'} 
                    color="success" 
                    size="small" 
                    sx={{ textTransform: 'capitalize' }} 
                  />
                </Box>
              </Box>
            </Box>
            <Box sx={{ mt: { xs: 2, md: 0 }, display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PersonAddIcon />}
                onClick={handleAddAgentOpen}
              >
                Add Agent
              </Button>
              {/* <Button variant="outlined" size="small" startIcon={<EditIcon />}>
                Edit Business
              </Button> */}
            </Box>
          </Box>
        </Paper>

        {/* Stats Cards */}
        <Grid2 container spacing={3} sx={{ mb: 4 }}>
          {statsData.map((card, index) => (
            <Grid2 size={{xs:12, sm:6, md:4}} key={index}>
              <Card sx={{ 
                borderRadius: 2, 
                height: '100%', 
                boxShadow: 2,
                transition: 'all 0.3s ease-in-out',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: theme => `0 12px 24px ${alpha(theme.palette.common.black, 0.15)}`,
                  '& .MuiAvatar-root': {
                    transform: 'scale(1.1)',
                  }
                },
                '& .MuiAvatar-root': {
                  transition: 'transform 0.3s ease-in-out',
                }
              }}>
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: alpha(card.color, 0.1), color: card.color }}>
                      {card.icon}
                    </Avatar>
                  }
                  title={
                    <Typography variant="h6" fontWeight="medium" component="div">
                      {card.title}
                    </Typography>
                  }
                />
                <CardContent sx={{ pt: 0 }}>
                  <Typography variant="h3" fontWeight="bold" component="div">
                    {card.value}
                  </Typography>
                  {(card.change || card.newClientsChip) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
                      {card.change && (
                        <Chip 
                          label={card.change}
                          color="info" 
                          size="small" 
                          sx={{ height: 20 }} 
                        />
                      )}
                      {card.newClientsChip && (
                        <Chip 
                          label={card.newClientsChip}
                          color="success" 
                          size="small" 
                          sx={{ height: 20 }} 
                        />
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid2>
          ))}
        </Grid2>

        {/* Tab Navigation */}
        <Box sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="business admin tabs">
            <Tab label="Agents" />
            <Tab label="Performance" />
            <Tab label="Recent Activity" />
          </Tabs>
        </Box>

        {/* Agents Tab */}
        {activeTab === 0 && (
          <AgentsTable
            agents={displayAgents}
            isLoading={isLoadingAgents}
            error={agentsError}
            totalAgents={totalAgents || 0}
            isAddingAgent={isAddingAgent}
            onAddAgent={handleAddAgentOpen}
            onEditAgent={handleEditAgentOpen}
          />
        )}

        {/* Performance Tab */}
        {activeTab === 1 && (
          <Grid2 container spacing={3}>
            <Grid2 size={{xs:12, md:8}}>
              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>Monthly Performance</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Chart would go here - showing listings, sales, and revenue over time
                </Typography>
                
                <Box sx={{ height: 200, bgcolor: alpha('#f5f5f5', 0.5), borderRadius: 1, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">Performance Chart Placeholder</Typography>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle1" gutterBottom>Key Metrics</Typography>
                <Grid2 container spacing={2}>
                  {[
                    { 
                      label: 'Total Agents', 
                      value: isLoadingStats ? '...' : (stats?.agents?.total_agents || MOCK_PERFORMANCE.totalListings)
                    },
                    { 
                      label: 'Active Agents', 
                      value: isLoadingStats ? '...' : (stats?.agents?.active_agents || MOCK_PERFORMANCE.soldLastMonth)
                    },
                    { 
                      label: 'Total Clients', 
                      value: isLoadingStats ? '...' : (stats?.clients?.total_clients || MOCK_PERFORMANCE.propertiesAnalyzedThisMonth)
                    },
                    { 
                      label: 'Active Clients', 
                      value: isLoadingStats ? '...' : (stats?.clients?.active_clients || MOCK_PERFORMANCE.avgPropertiesPerAgent)
                    },
                    { 
                      label: 'Homes (30d)', 
                      value: isLoadingStats ? '...' : (stats?.homes?.homes_last_30_days || `${MOCK_PERFORMANCE.growth}%`)
                    },
                    { 
                      label: 'Active Homes', 
                      value: isLoadingStats ? '...' : (stats?.homes?.active_homes_last_30_days || `${MOCK_PERFORMANCE.clientSatisfaction}%`)
                    }
                  ].map((metric, index) => (
                    <Grid2 size={{xs:6, sm:2}} key={index}>
                      <Box sx={{ textAlign: 'center', p: 1 }}>
                        <Typography variant="h5" fontWeight="medium">{metric.value}</Typography>
                        <Typography variant="body2" color="text.secondary">{metric.label}</Typography>
                      </Box>
                    </Grid2>
                  ))}
                </Grid2>
              </Paper>
            </Grid2>
            
            <Grid2 size={{xs:12, md:4}}>
              <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Top Performing Agents</Typography>
                
                {MOCK_AGENTS
                  .sort((a, b) => b.walkthroughs30d - a.walkthroughs30d)
                  .slice(0, 3)
                  .map((agent, index) => (
                    <Box key={agent.id} sx={{ mt: index > 0 ? 2 : 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, mr: 1.5 }}>{agent.name.charAt(0)}</Avatar>
                        <Typography variant="body1">{agent.name}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">Walkthroughs (30d)</Typography>
                        <Typography variant="body2" fontWeight="medium">{agent.walkthroughs30d}</Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={(agent.walkthroughs30d / 25) * 100}
                        sx={{ mb: 1, borderRadius: 1, height: 6 }}
                      />
                      {index < 2 && <Divider sx={{ mt: 2 }} />}
                    </Box>
                  ))}
              </Paper>
              
              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>Client Satisfaction</Typography>
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h3" color="success.main" fontWeight="bold">
                    {MOCK_PERFORMANCE.clientSatisfaction}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Average client rating</Typography>
                  
                  <Box sx={{ mt: 3, p: 2, bgcolor: alpha('#f5f5f5', 0.5), borderRadius: 1 }}>
                    <Typography variant="body2">
                      Client satisfaction is above industry average of 87%
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid2>
          </Grid2>
        )}

        {/* Recent Activity Tab */}
        {activeTab === 2 && (
          <RecentActivityTable />
        )}
      </Container>

      {/* Add Agent Modal */}
      <AddAgentForm
        open={addAgentModalOpen}
        onClose={handleAddAgentClose}
        onSubmit={handleAddAgentSubmit}
        isSubmitting={isAddingAgent}
      />

      {/* Edit Agent Modal */}
      <AddAgentForm
        open={editAgentModalOpen}
        onClose={handleEditAgentClose}
        onSubmit={handleAddAgentSubmit} // Not used in edit mode
        onUpdate={handleEditAgentSubmit}
        isSubmitting={isUpdatingAgent}
        isEdit={true}
        editAgent={editingAgent || undefined}
      />
    </BasePage>
  );
};

export default BusinessAdminPage;